import React, { useEffect, useState } from "react";
import { Box, Typography, makeStyles, FormGroup, Checkbox, FormControlLabel } from "@material-ui/core";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AppInput from "components/Input/AppInput";
import AppButton from "components/CustomButtons/AppButton";
import CustomDatePicker from "components/Input/CustomDatePicker";
import { updateWorkAndPay, fetchWorkerDetail } from "store/slices/worker";
import AppColor from "utilities/AppColor";
import { YupValidations } from "utils/validate";
import SelectField from "components/Select/SelectField";
import AppConfig, { ACCOUNTING_TYPE, WORKER_TYPE, EMPLOYMENT_TYPE, APPROVAL_STATUS } from "config/AppConfig";
import { getAuthInfo } from "store/slices/auth";
import { fetchSuperFunds } from "store/slices/worker";
import { SYSTEM_ROLE } from "config/AppConfig";
import { fetchSupports } from "store/slices/worker";

const { PATTERNS } = AppConfig;

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "#0D9488",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 15,
    marginBottom: 16,
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
    marginBottom: 20
  }
}));

const WorkPayTab = ({ staffDetail, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(getAuthInfo);
  const { accountingType } = authInfo;
  const isAdmin = authInfo?.role === SYSTEM_ROLE.COMPANY;

  const [superFunds, setSuperFunds] = useState([]);
  const [initForm, setInitForm] = useState({
    accountName: '',
    bankName: '',
    bankAccountNumber: '',
    superannuationSuperProviderId: '',
    superannuationAccountNumber: '',
    bankBsbNumber: '',
    taxNumber: '',
    isTaxFreeThresholdClaimed: false,
    supportId: ""
  });
  const [supportList, setSupportList] = useState([]);

  const {
    id = null,
    role = "",
    employmentType = "",
    primaryHouseType = "", // Dummy
    startDate = "",
    payRate = "", // Dummy
    payrollId = "", // Dummy
    bankName = "",
    accountName = "",
    bankAccountNumber = "",
    superannuationSuperProviderId = "",
    taxNumber = "",
    isTaxFreeThresholdClaimed = false,
    superannuationAccountNumber = "",
    bankBsbNumber = "",
    supportId = ""
  } = staffDetail || {};

  const validationSchema = Yup.object().shape({
    accountName: Yup.string()
      .trim()
      .required("validation.required")
      .max(32, "validation.max_length_32")
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    bankName: Yup.string().trim()
      .required("validation.required")
      .max(32, "validation.max_length_32")
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    employmentType: YupValidations.requiredString,
    supportId: YupValidations.requiredString,
    bankAccountNumber: YupValidations.requiredString.max(9, { message: "validation.max_length", value: 9 }),
    superannuationSuperProviderId: accountingType === ACCOUNTING_TYPE.XERO ? YupValidations.requiredString : Yup.string(),
    superannuationAccountNumber: accountingType === ACCOUNTING_TYPE.XERO
      ? YupValidations.requiredString.max(20, { message: "validation.max_length", value: 20 })
      : Yup.string().max(20, { message: "validation.max_length", value: 20 }),
    bankBsbNumber: YupValidations.requiredString.min(6, { message: "validation.min_length", value: 6 }).max(7, { message: "validation.max_length", value: 7 }),
    taxNumber: YupValidations.requiredString.min(8, { message: "validation.min_length", value: 8 }).max(9, { message: "validation.max_length", value: 9 })
  });

  const loadSuperFunds = async () => {
    const result = await dispatch(fetchSuperFunds());
    if (result && result.payload) {
      setSuperFunds(result.payload);
    }
  };

  useEffect(() => {
    if (staffDetail && Object.keys(staffDetail).length > 0) {
      const initial = {
        role: role,
        employmentType,
        primaryHouseType,
        startDate,
        payRate,
        payrollId,
        bankName,
        accountName,
        bankAccountNumber,
        superannuationSuperProviderId,
        taxNumber,
        isTaxFreeThresholdClaimed,
        superannuationAccountNumber,
        bankBsbNumber,
        supportId: staffDetail?.supports[0]?.id || ''
      };

      setInitForm(initial);
    }
  }, [staffDetail]);

  useEffect(() => {
    if (accountingType === ACCOUNTING_TYPE.XERO) {
      loadSuperFunds();
    }
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      id,
      role: values.role,
      employmentType: values.employmentType,
      startDate: values.startDate,
      bankName: values.bankName,
      accountName: values.accountName,
      bankAccountNumber: values.bankAccountNumber,
      superannuationSuperProviderId: values.superannuationSuperProviderId,
      taxNumber: values.taxNumber,
      isTaxFreeThresholdClaimed: values.isTaxFreeThresholdClaimed,
      superannuationAccountNumber: values.superannuationAccountNumber,
      bankBsbNumber: values.bankBsbNumber,
      supportId: values.supportId
    };

    const result = await dispatch(updateWorkAndPay(payload));
    if (updateWorkAndPay.fulfilled.match(result)) {
      if (onChange && id) {
        // Refresh staff detail
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setSubmitting(false);
  };

  const workerTypeOptions = [
    { value: WORKER_TYPE.NURSE, label: t(`worker_type.${WORKER_TYPE.NURSE}`) },
    { value: WORKER_TYPE.SUPPORT_WORKER, label: t(`worker_type.${WORKER_TYPE.SUPPORT_WORKER}`) },
    { value: WORKER_TYPE.SENIOR_SUPPORT_WORKER, label: t(`worker_type.${WORKER_TYPE.SENIOR_SUPPORT_WORKER}`) },
    { value: WORKER_TYPE.TEAM_LEADER, label: t(`worker_type.${WORKER_TYPE.TEAM_LEADER}`) },
    { value: WORKER_TYPE.HOUSE_COORDINATOR, label: t(`worker_type.${WORKER_TYPE.HOUSE_COORDINATOR}`) },
    { value: WORKER_TYPE.REGISTERED_NURSE, label: t(`worker_type.${WORKER_TYPE.REGISTERED_NURSE}`) },
    { value: WORKER_TYPE.CLINICAL_NURSE_MANAGER, label: t(`worker_type.${WORKER_TYPE.CLINICAL_NURSE_MANAGER}`) },
    { value: WORKER_TYPE.OPERATIONS_MANAGER, label: t(`worker_type.${WORKER_TYPE.OPERATIONS_MANAGER}`) },
    { value: WORKER_TYPE.HR_MANAGER, label: t(`worker_type.${WORKER_TYPE.HR_MANAGER}`) },
    { value: WORKER_TYPE.DIRECTOR, label: t(`worker_type.${WORKER_TYPE.DIRECTOR}`) },


  ];

  const employmentTypeOptions = [
    { value: EMPLOYMENT_TYPE.CASUAL, label: t(`employmentType.${EMPLOYMENT_TYPE.CASUAL}`, "Casual") },
    { value: EMPLOYMENT_TYPE.PART_TIME, label: t(`employmentType.${EMPLOYMENT_TYPE.PART_TIME}`, "Part-time") },
    { value: EMPLOYMENT_TYPE.FULL_TIME, label: t(`employmentType.${EMPLOYMENT_TYPE.FULL_TIME}`, "Full-time") }
  ];

  // const canEditStartDate = approvalStatus === APPROVAL_STATUS.ACTIVE.id;
  const getSupportList = async () => {
    const res = await dispatch(fetchSupports());
    if (res.payload) {
      setSupportList(res.payload.map(item => ({ value: item.id, label: item.name })));
    }
  };

  useEffect(() => {
    getSupportList();
  }, []);
  return (
    <Box>
      <Formik
        enableReinitialize={true}
        initialValues={initForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => {
          console.log('errors', errors);
          return (
            <Form>
              <Typography className={classes.sectionTitle}>
                {t("title.roleAssignment")}
              </Typography>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={SelectField}
                    name="role"
                    label={"label.role"}
                    options={workerTypeOptions}
                    placeholder={t("label.role")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={SelectField}
                    name="supportId"
                    label={"label.support"}
                    options={supportList}
                    placeholder={t("label.support")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={SelectField}
                    name="employmentType"
                    label={"label.employmentType"}
                    options={employmentTypeOptions}
                    placeholder={t("label.employmentType")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="primaryHouseType"
                    label={t("label.primaryHouse")}
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={CustomDatePicker}
                    name="startDate"
                    type="text"
                    id="startDate"
                    label={"label.startDate"}
                    formatDateJob={true}
                    disabled={!isAdmin}
                  />
                </div>
              </div>

              <Typography className={classes.sectionTitle}>
                {t("title.payrollFinancial")}
              </Typography>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="payRate"
                    label={t("label.payRateCasual")}
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="payrollId"
                    label={t("label.payrollId")}
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="bankName"
                    label={t("label.bankName")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="accountName"
                    label={t("label.accountName")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="bankBsbNumber"
                    label={t("label.bsbNumber")}
                    required
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="bankAccountNumber"
                    label={t("label.accountNumber")}
                    required
                  />
                </div>
              </div>

              {accountingType === ACCOUNTING_TYPE.XERO &&
                <>
                  <Typography className={classes.sectionTitle}>
                    {t('text.superannuation')}
                  </Typography>
                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={SelectField}
                        name="superannuationSuperProviderId"
                        label={"label.superProvider"}
                        required
                        options={superFunds.map(item => {
                          return {
                            value: item.superFundId,
                            label: item.name
                          }
                        })}
                      />
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <Field
                        component={AppInput}
                        name="superannuationAccountNumber"
                        label={"label.account#"}
                        type="tel"
                        mask={'99999999999999999999'}
                        maskChar=""
                        required
                      />
                    </div>
                  </div>
                </>
              }

              <Typography className={classes.sectionTitle}>
                {t("title.taxDeclaration")}
              </Typography>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <Field
                    component={AppInput}
                    name="taxNumber"
                    type="tel"
                    label={t("label.taxNumber")}
                    mask={'99999999999999999999'}
                    maskChar=""
                    required
                  />
                </div>
              </div>

              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isTaxFreeThresholdClaimed}
                      onChange={(e) => setFieldValue('isTaxFreeThresholdClaimed', e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{t("label.taxFreeThreshold")}</span>}
                />
              </Box>

              <FormGroup className={classes.control}>
                <AppButton
                  label={t("button.save_changes")}
                  rounder
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                  buttonColor={AppColor.mainColor}
                />
              </FormGroup>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

WorkPayTab.propTypes = {
  staffDetail: PropTypes.object,
  onChange: PropTypes.func,
};

export default WorkPayTab;
